import React from "react";
import {Stack, Typography, Tooltip} from "@mui/material";
import {ColletteDivider} from "@collette-travel/collette-common-controls";
import KeyTourInfoSkeletonColumn from "./key-tour-info.skeleton.column";
import {ColletteTourOverviewHeaderProps} from "../types";
import KeyTourInfoValue from "../key-tour-info-value";
import InfoIcon from "../info-icon";

const KeyTourInfoColumn = ({
  testId,
  details,
  isLoading,
  labelColor = "primary",
  showDividers = true,
  infoIconVariant = "outlined",
  sxOverride = {}
}: ColletteTourOverviewHeaderProps) => {

  const handleKeyUp = (callback?: () => void) => (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (callback && (e.key === "Enter")) {
      callback();
    }
  };

  if (isLoading) {
    return (
      <KeyTourInfoSkeletonColumn labelColor={labelColor} details={details} testId={testId} />
    );
  }

  return (
    <Stack
      sx={{
        flexDirection: "column",
        display: "flex",
        gap: "0.8rem",
        width: "100%",
        minWidth: "30rem",
        justifyContent: "space-between",
        ...sxOverride
      }}
      data-testid="column-tour-header-section"
    >
      {details.map((detail, index) => (
        <Stack direction="column" gap="0.8rem" key={detail.id}>
          <Stack
            sx={{
              gap: "1.2rem",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              position: "relative"
            }}
            data-testid={`column-header-${detail.id}-box`}
          >
            <Stack
              gap="0.4rem"
              sx={{
                alignSelf: { xs: "start", sm: "center" }
              }}
            >
              <Tooltip
                title={detail.tooltipText}
                placement="top-start"
                arrow
              >
              <Stack
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "0.4rem",
                  zIndex: 3,
                  ...(detail.onClick ? {
                    cursor: "pointer"
                  } : {}),
                  "&::after": {
                    content: "\"\"",
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0
                  },
                  "&:focus-visible": {
                    outlineOffset: 2
                  }
                }}
                tabIndex={detail.onClick ? 0 : -1}
                onClick={detail.onClick}
                onKeyUp={handleKeyUp(detail.onClick)}
                data-testid={`column-header-${detail.id}-box-label`}
              >
                <Typography
                  color={labelColor}
                  sx={{
                    textTransform: "uppercase",
                    fontSize: "1.6rem",
                    fontWeight: 700,
                    lineHeight: "150%",
                    textWrap: "nowrap"
                  }}
                  data-testid={`column-header-${detail.id}-label`}
                >
                  {detail.label}
                </Typography>
                {detail.tooltipText && (
                  <InfoIcon
                    variant={infoIconVariant}
                    color={labelColor === "primary" || labelColor === "secondary" ? labelColor : undefined}
                    sx={{
                      height: "2rem",
                      width: "2rem",
                      color: labelColor === "text.disabled" ? "text.disabled" : undefined,
                      mb: "0.2rem"
                    }}
                  />
                )}
              </Stack>
                </Tooltip>
              {detail.subLabel && (
                <Tooltip
                  title={detail.subLabelTooltipText}
                  arrow
                >
                  <Stack
                    sx={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "0.4rem",
                      zIndex: 4,
                      ...(detail.subLabelOnClick ? {
                        cursor: "pointer"
                      } : {}),
                      "&:focus-visible": {
                        outlineOffset: 2
                      }
                    }}
                    onClick={detail.subLabelOnClick}
                    onKeyUp={handleKeyUp(detail.subLabelOnClick)}
                    tabIndex={detail.subLabelOnClick ? 0 : -1}
                    data-testid={`column-header-${detail.id}-sub-label`}
                  >
                    <Typography
                      sx={{
                        color: "text.secondary",
                        fontWeight: 400,
                        textTransform: "capitalize",
                        fontSize: "1.4rem",
                        textWrap: "nowrap"
                      }}
                    >
                      {detail.subLabel}
                    </Typography>
                    {detail.subLabelTooltipText && (
                      <InfoIcon
                        variant={infoIconVariant}
                        sx={{
                          color: "text.secondary",
                          width: "1.6rem",
                          height: "1.6rem",
                          mb: "0.2rem"
                        }}
                      />
                    )}
                  </Stack>
                </Tooltip>
              )}
            </Stack>
            <KeyTourInfoValue value={detail.value} testId={testId} detailId={detail.id} orientation="column" />
          </Stack>
          {showDividers && index !== details.length - 1 && (
            <ColletteDivider
              orientation="horizontal"
              sx={{ borderColor: "divider" }}
            />
          )}
        </Stack>
      ))}
    </Stack>
  );
};

export default KeyTourInfoColumn;